import { Injectable, OnDestroy } from '@angular/core';
import { FLEX_MONITOR_STATUS_ENTITY_IDS } from 'app/shared/constant';
import type { OptimizedViewData } from 'app/shared/models/view-data';
import { INSTALLATION_TYPE } from 'app/shared/models/location-details';
import type { SignalRMessage } from 'app/shared/models/signalr-message';
import type {
    FlexDiagnosticCommandResponse,
    MonitorDiagnosticLog,
} from 'app/shared/services/monitor-diagnostics.service';
import type { SensorConfigurationCollection } from 'app/shared/components/location-card/store/models/dynamic-section-models';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    exhaustMap,
    finalize,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import type { FlexDiagnosticsDialogData } from '../flex-diagnostics-dialog.component';
import {
    hasFlexInstallationDrawingData,
    isRecentFlexDiagnostic,
    selectFlexMp1Location,
} from './flex-monitor-status.mapper';
import { FlexMonitorStatusDataSource } from './flex-monitor-status.data-source';
import type {
    FlexDateContext,
    FlexDiagnosticState,
    FlexInstallationDrawingData,
    FlexMonitorStatusState,
    FlexResource,
    FlexSensorDiagnostic,
} from './flex-monitor-status.models';

const CUSTOMER_TIME_OFFSET_MARGIN_MS = 24 * 60 * 60 * 1000;
const LATEST_DIAGNOSTIC_LOOKBACK_MS = 90 * 24 * 60 * 60 * 1000;

@Injectable()
export class FlexMonitorStatusFacade implements OnDestroy {
    public readonly state$ = new BehaviorSubject<FlexMonitorStatusState>(createInitialState());

    private readonly loadRequests$ = new Subject<boolean>();
    private readonly queueRequests$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    constructor(private dataSource: FlexMonitorStatusDataSource) {
        this.loadRequests$
            .pipe(
                exhaustMap((isRefresh) => this.load(isRefresh)),
                takeUntil(this.destroy$),
            )
            .subscribe((result) => {
                this.applyResult(result);
                if (this.state$.value.queueState === 'idle') this.queueRequests$.next();
            });

        this.queueRequests$
            .pipe(
                exhaustMap(() => this.queueDiagnostics()),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.dataSource
            .getDiagnosticNotifications()
            .pipe(
                mergeMap((message) => this.completeDiagnostic(message)),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    private dialogData: FlexDiagnosticsDialogData | null = null;

    public initialize(dialogData: FlexDiagnosticsDialogData): void {
        if (this.dialogData) return;

        this.dialogData = dialogData;
        this.loadRequests$.next(false);
    }

    public refresh(): void {
        const current = this.state$.value;
        if (
            !this.dialogData ||
            current.isLoading ||
            current.queueState === 'queueing' ||
            current.diagnostics.some((diagnostic) => diagnostic.state === 'running')
        ) {
            return;
        }
        this.queueRequests$.next();
        this.loadRequests$.next(true);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.loadRequests$.complete();
        this.queueRequests$.complete();
        this.state$.complete();
    }

    private load(isRefresh: boolean): Observable<FlexMonitorStatusState> {
        this.setLoadingState(isRefresh);
        const dialogData = this.getDialogData();

        const status$ = this.toResource(
            this.dataSource.getStatus(dialogData.customerId, dialogData.locationId),
            (value) => !value,
        );
        const configuration$ = this.toResource(
            this.dataSource.getConfiguration(dialogData.customerId, dialogData.locationId),
            (value) => !value,
        );
        const location$ = this.toResource(
            this.dataSource
                .getLocationDetails(dialogData.customerId, dialogData.locationId)
                .pipe(map((details) => selectFlexMp1Location(details))),
            (value) => !value,
        ).pipe(shareReplay(1));
        const installationDrawing$ = location$.pipe(
            switchMap((location) => {
                if (location.state !== 'ready' || !location.data) {
                    return of<FlexResource<FlexInstallationDrawingData>>({ state: 'blocked', data: null });
                }
                if (location.data.installationType !== INSTALLATION_TYPE.PIPE) {
                    return of<FlexResource<FlexInstallationDrawingData>>({ state: 'empty', data: null });
                }

                return this.toResource(
                    this.dataSource.getInstallationDrawing(dialogData.customerId, dialogData.locationId),
                    (value) => !hasFlexInstallationDrawingData(value),
                );
            }),
        );
        const sensors$ = location$.pipe(
            switchMap((location) => {
                const deviceId = location.data?.serialNumber?.trim();
                if (location.state !== 'ready' || !deviceId) {
                    return of<FlexResource<SensorConfigurationCollection>>({ state: 'blocked', data: null });
                }

                return this.toResource(
                    this.dataSource.getSensors(deviceId, dialogData.customerId),
                    (value) => !value?.sensors?.length,
                );
            }),
        );
        const hydrograph$ = dialogData.canViewRawData
            ? this.toResource(
                  this.dataSource.getLatestHydrograph(
                      dialogData.customerId,
                      dialogData.locationId,
                      FLEX_MONITOR_STATUS_ENTITY_IDS,
                  ),
                  (value) => !value,
              )
            : of<FlexResource<OptimizedViewData>>({ state: 'notAuthorized', data: null });
        const dateContext$ = this.toResource(this.dataSource.getDateContext(dialogData.customerId), (value) => !value);
        const diagnosticUnits$ = this.toResource(
            this.dataSource.getDiagnosticUnits(dialogData.customerId),
            (value) => !value?.length,
        );

        return forkJoin({
            status: status$,
            configuration: configuration$,
            location: location$,
            sensors: sensors$,
            hydrograph: hydrograph$,
            installationDrawing: installationDrawing$,
            dateContext: dateContext$,
            diagnosticUnits: diagnosticUnits$,
            diagnostics: isRefresh ? of(this.state$.value.diagnostics) : this.loadLatestDiagnostics(),
        }).pipe(
            map((resources) => ({
                ...resources,
                isLoading: false,
                isRefreshing: false,
                queueState: this.state$.value.queueState,
                diagnostics: isRefresh ? this.state$.value.diagnostics : resources.diagnostics,
            })),
            finalize(() => {
                if (this.state$.value.isLoading) {
                    this.state$.next({ ...this.state$.value, isLoading: false, isRefreshing: false });
                }
            }),
        );
    }

    private loadLatestDiagnostics(): Observable<FlexSensorDiagnostic[]> {
        const dialogData = this.getDialogData();
        const now = Date.now();

        return this.dataSource
            .getDiagnosticLogs(dialogData.customerId, dialogData.locationId, {
                startTime: new Date(now - LATEST_DIAGNOSTIC_LOOKBACK_MS),
                endTime: new Date(now + CUSTOMER_TIME_OFFSET_MARGIN_MS),
            })
            .pipe(
                catchError(() => of<MonitorDiagnosticLog[]>([])),
                map((logs) => selectLatestDiagnostics(logs ?? [])),
            );
    }

    private queueDiagnostics(): Observable<FlexDiagnosticCommandResponse[] | null> {
        const current = this.state$.value;
        const requestedAt = Date.now();
        this.state$.next({ ...current, queueState: 'queueing' });
        const dialogData = this.getDialogData();

        return this.dataSource.queueDiagnostics(dialogData.customerId, dialogData.locationId).pipe(
            tap((commands) =>
                this.state$.next({
                    ...this.state$.value,
                    queueState: 'queued',
                    diagnostics: (commands ?? []).map(
                        (command): FlexSensorDiagnostic => ({
                            commandId: command.commandId,
                            modbusSensorId: command.modbusSensorId,
                            state: 'running',
                            log: null,
                            previousLog: selectPreviousLog(
                                current.diagnostics,
                                command.modbusSensorId,
                                current.dateContext.data,
                                requestedAt,
                            ),
                        }),
                    ),
                }),
            ),
            catchError(() => {
                this.state$.next({ ...this.state$.value, queueState: 'error' });
                return of(null);
            }),
        );
    }

    private completeDiagnostic(message: SignalRMessage): Observable<void> {
        const details = message.flexDiagnostic;
        const dialogData = this.dialogData;
        if (!details || !dialogData) return EMPTY;

        const diagnostic = this.state$.value.diagnostics.find(
            (item) => item.state === 'running' && isDiagnosticMatch(item, details.commandId, details.modbusSensorId),
        );
        if (!diagnostic) return EMPTY;

        if (!details.success) {
            this.updateDiagnostic(details.commandId, 'failed', null);
            return EMPTY;
        }

        const now = Date.now();
        return this.dataSource
            .getDiagnosticLogs(dialogData.customerId, dialogData.locationId, {
                startTime: new Date(now - CUSTOMER_TIME_OFFSET_MARGIN_MS),
                endTime: new Date(now + CUSTOMER_TIME_OFFSET_MARGIN_MS),
            })
            .pipe(
                map(
                    (logs) =>
                        logs?.find((log) => isDiagnosticMatch(diagnostic, log.commandId, log.modbusSensorId)) ?? null,
                ),
                catchError(() => of(null)),
                map((log) => this.updateDiagnostic(details.commandId, 'completed', log)),
                tap(() => this.loadRequests$.next(true)),
            );
    }

    private updateDiagnostic(commandId: string, state: FlexDiagnosticState, log: MonitorDiagnosticLog | null): void {
        const current = this.state$.value;
        this.state$.next({
            ...current,
            diagnostics: current.diagnostics.map((diagnostic) =>
                diagnostic.commandId === commandId ? { ...diagnostic, state, log } : diagnostic,
            ),
        });
    }

    private toResource<T>(source: Observable<T>, isEmpty: (value: T) => boolean): Observable<FlexResource<T>> {
        return source.pipe(
            map((value): FlexResource<T> => ({ state: isEmpty(value) ? 'empty' : 'ready', data: value })),
            catchError(() => of<FlexResource<T>>({ state: 'error', data: null })),
        );
    }

    private setLoadingState(isRefresh: boolean): void {
        const current = this.state$.value;
        this.state$.next({
            status: loadingResource(current.status),
            location: loadingResource(current.location),
            configuration: loadingResource(current.configuration),
            sensors: loadingResource(current.sensors),
            hydrograph: this.getDialogData().canViewRawData
                ? loadingResource(current.hydrograph)
                : { state: 'notAuthorized', data: null },
            installationDrawing: loadingResource(current.installationDrawing),
            dateContext: loadingResource(current.dateContext),
            diagnosticUnits: loadingResource(current.diagnosticUnits),
            isLoading: true,
            isRefreshing: isRefresh,
            queueState: current.queueState,
            diagnostics: current.diagnostics,
        });
    }

    private getDialogData(): FlexDiagnosticsDialogData {
        if (!this.dialogData) throw new Error('FLEX Monitor Status dialog data is unavailable.');
        return this.dialogData;
    }

    private applyResult(result: FlexMonitorStatusState): void {
        const previous = this.state$.value;
        this.state$.next({
            ...result,
            status: retainPreviousOnError(result.status, previous.status),
            location: retainPreviousOnError(result.location, previous.location),
            configuration: retainPreviousOnError(result.configuration, previous.configuration),
            sensors: retainPreviousOnError(result.sensors, previous.sensors),
            hydrograph: retainPreviousOnError(result.hydrograph, previous.hydrograph),
            installationDrawing: retainPreviousOnError(result.installationDrawing, previous.installationDrawing),
            dateContext: retainPreviousOnError(result.dateContext, previous.dateContext),
            diagnosticUnits: retainPreviousOnError(result.diagnosticUnits, previous.diagnosticUnits),
            queueState: previous.queueState,
            diagnostics: previous.queueState === 'idle' ? result.diagnostics : previous.diagnostics,
        });
    }
}

function createInitialState(): FlexMonitorStatusState {
    return {
        status: { state: 'idle', data: null },
        location: { state: 'idle', data: null },
        configuration: { state: 'idle', data: null },
        sensors: { state: 'idle', data: null },
        hydrograph: { state: 'idle', data: null },
        installationDrawing: { state: 'idle', data: null },
        dateContext: { state: 'idle', data: null },
        diagnosticUnits: { state: 'idle', data: null },
        isLoading: false,
        isRefreshing: false,
        queueState: 'idle',
        diagnostics: [],
    };
}

function loadingResource<T>(resource: FlexResource<T>): FlexResource<T> {
    return { state: 'loading', data: resource.data };
}

function retainPreviousOnError<T>(current: FlexResource<T>, previous: FlexResource<T>): FlexResource<T> {
    return current.state === 'error' && previous.data ? { ...current, data: previous.data } : current;
}

function isDiagnosticMatch(
    diagnostic: FlexSensorDiagnostic,
    commandId: string | null | undefined,
    modbusSensorId: number | null | undefined,
): boolean {
    return (
        commandId === diagnostic.commandId &&
        (modbusSensorId === null || modbusSensorId === undefined || modbusSensorId === diagnostic.modbusSensorId)
    );
}

function selectLatestDiagnostics(logs: MonitorDiagnosticLog[]): FlexSensorDiagnostic[] {
    const diagnostics: FlexSensorDiagnostic[] = [];
    for (const log of logs) {
        const modbusSensorId = log.modbusSensorId;
        if (modbusSensorId === null || modbusSensorId === undefined) continue;
        if (diagnostics.some((diagnostic) => diagnostic.modbusSensorId === modbusSensorId)) continue;

        diagnostics.push({
            commandId: null,
            modbusSensorId,
            state: 'completed',
            log,
            previousLog: null,
        });
    }
    return diagnostics;
}

function selectPreviousLog(
    diagnostics: FlexSensorDiagnostic[],
    modbusSensorId: number,
    dateContext: FlexDateContext | null,
    requestedAt: number,
): MonitorDiagnosticLog | null {
    const diagnostic = diagnostics.find((item) => item.modbusSensorId === modbusSensorId);
    const log = diagnostic?.log ?? diagnostic?.previousLog ?? null;
    return isRecentFlexDiagnostic(log?.status.currentMonitorTime, dateContext, requestedAt) ? log : null;
}
